import { TFunction } from 'i18next';

import i18n from '@app/dashboard/i18n';

import {
  emailValidation,
  numbersOnly,
  optionalButHasValidation,
  phoneNumberValidation,
  required,
} from '@app/dashboard/utils/formValidations';
import { TCountry } from '@app/dashboard/types/outerLayout/common';

const BASE_FIELDS_DATA = { value: '', isValid: false, errorMsg: '' };

export const CUSTOMER_DETAILS_TYPES = {
  ONE: 'one',
  MULTIPLE: 'multiple',
} as const;

export const MANDATORY_CUSTOMER_INFO_KEYS = {
  PHONE: 'phone_number',
  EMAIL: 'email',
  BOTH: 'both',
};

export const ADDITIONAL_INFO_KEYS = {
  CUSTOMER_NAME: 'customer_name',
  SHIPPING_ADDRESS: 'shipping_address',
  BILLING_ADDRESS: 'billing_address',
} as const;

export const CUSTOMER_DETAILS_SECTION_ITEMS = (t: TFunction) => [
  {
    id: CUSTOMER_DETAILS_TYPES.ONE,
    text: t(
      'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.oneCustomer'
    ),
    value: CUSTOMER_DETAILS_TYPES.ONE,
  },
  {
    id: CUSTOMER_DETAILS_TYPES.MULTIPLE,
    text: t(
      'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.multipleCustomers'
    ),
    value: CUSTOMER_DETAILS_TYPES.MULTIPLE,
  },
];

export const SINGLE_PAYMENTS_FIELDS = {
  FULL_NAME: 'full_name',
  EMAIL: 'email',
  PHONE_NUMBER: 'phone_number',
  DATE: 'date',
  TIME: 'time',
  PAYMENT_LIMIT: 'max_attempts',
  IMAGE: 'payment_link_image',
  DESCRIPTION: 'description',
  REF_ID: 'reference_id',
  FIXED_AMOUNT: 'amount_cents',
  SAVE_SELECTIONS: 'save_selection',
  PAYMENT_METHODS: 'payment_methods',
  COLLECT_PHONE_NUMBER: 'collect_phone_number',
  COLLECT_EMAIL_ADDRESS: 'collect_email_address',
  COLLECT_CUSTOMER_NAME: 'collect_customer_name',
  COLLECT_BILLING_DETAILS: 'collect_billing_details',
  COLLECT_SHIPPING_DETAILS: 'collect_shipping_details',
  ALLOWED_COUNTRIES: 'allowed_countries',
};

export const MAX_LOGO_SIZE = 5 * 1024 * 1024; // 5MB
export const ALLOWED_FORMATS = ['.jpeg', '.png', '.jpg'];
export const MAX_DIMENSIONS = {
  WIDTH: 3000,
  HEIGHT: 3000,
};

const FULL_NAME_REGEX =
  /^[\u0600-\u06FF\u0750-\u077F\uFB50-\uFBC1\uFBD3-\uFDFF\uFE70-\uFEFCa-zA-Z\s']{0,30}$/;

const DESCRIPTION_REGEX =
  /^$|^(?=[\s\S]{1,500}$)[\u0600-\u06FF\u0750-\u077F\uFB50-\uFBC1\uFBD3-\uFDFF\uFE70-\uFEFCa-zA-Z0-9\s.,'~!@#$%^&*()_\-+={}[\]|\\<>?":;]+$/;
const DISALLOWED_HTML_REGEX = /^(?![\s\S]*<[^>]+>)[\s\S]*$/;

export const ONE_CUSTOMER_FIELDS_PLACEHOLDERS = {
  [SINGLE_PAYMENTS_FIELDS.FULL_NAME]: 'ex. Youssef Saeed',
  [SINGLE_PAYMENTS_FIELDS.EMAIL]: 'mail@example.com',
  [SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER]: '10 000 000 00',
};

export const MULTIPLE_CUSTOMERS_FIELDS = {
  MULTIPLE_CUSTOMERS: 'multipleCustomers',
  SHARE_WITH: 'share_with',
  IS_MULTIPLE: 'is_multiple',
  IS_UNLIMITED: 'is_unlimited',
};

export const INPUT_STATUS = {
  DEFAULT: 'default',
  ERROR: 'error',
  WARNING: 'warning',
} as const;

export const REQUIRED_REGEX = /^(?!\s*$).+/;
export const DATE_PLACE_HOLDER = {
  EN: 'dd-mm-yyyy',
  AR: 'يوم-شهر-سنة',
};

export const DEFAULT_TIME = {
  hour: '11',
  minute: '59',
  ampm: 'PM',
} as const;

export const PAYMENT_INITIAL_VALUES = {
  [SINGLE_PAYMENTS_FIELDS.FIXED_AMOUNT]: {
    ...BASE_FIELDS_DATA,
    validations: [],
  },
  [SINGLE_PAYMENTS_FIELDS.SAVE_SELECTIONS]: {
    ...BASE_FIELDS_DATA,
    validations: [],
  },
  [SINGLE_PAYMENTS_FIELDS.PAYMENT_METHODS]: {
    ...BASE_FIELDS_DATA,
    validations: [],
  },
};

export const ONE_CUSTOMER_FIELD_INITIAL_VALUES = (country: TCountry) => ({
  [SINGLE_PAYMENTS_FIELDS.FULL_NAME]: {
    ...BASE_FIELDS_DATA,
    validations: [
      {
        pattern: /^.{0,30}$/,
        error_msg: i18n.t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.errors.nameLengthError'
        ),
      },
      {
        pattern: FULL_NAME_REGEX,
        error_msg: i18n.t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.errors.nameLangError'
        ),
      },
    ],
  },
  [SINGLE_PAYMENTS_FIELDS.EMAIL]: {
    ...BASE_FIELDS_DATA,
    validations: [optionalButHasValidation(emailValidation)],
  },
  [SINGLE_PAYMENTS_FIELDS.PHONE_NUMBER]: {
    ...BASE_FIELDS_DATA,
    validations: [optionalButHasValidation(phoneNumberValidation(country))],
  },
});

export const CUSTOMER_INFO_FIELD_MAP = {
  [MANDATORY_CUSTOMER_INFO_KEYS.PHONE]: [
    SINGLE_PAYMENTS_FIELDS.COLLECT_PHONE_NUMBER,
  ],
  [MANDATORY_CUSTOMER_INFO_KEYS.EMAIL]: [
    SINGLE_PAYMENTS_FIELDS.COLLECT_EMAIL_ADDRESS,
  ],
  [MANDATORY_CUSTOMER_INFO_KEYS.BOTH]: [
    SINGLE_PAYMENTS_FIELDS.COLLECT_PHONE_NUMBER,
    SINGLE_PAYMENTS_FIELDS.COLLECT_EMAIL_ADDRESS,
  ],
};

export const ALL_CUSTOMER_INFO_FIELDS = [
  SINGLE_PAYMENTS_FIELDS.COLLECT_PHONE_NUMBER,
  SINGLE_PAYMENTS_FIELDS.COLLECT_EMAIL_ADDRESS,
];

export const MANDATORY_CUSTOMER_INFO = (t: TFunction) => [
  {
    id: MANDATORY_CUSTOMER_INFO_KEYS.PHONE,
    text: t(
      'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.mobile'
    ),
    value: MANDATORY_CUSTOMER_INFO_KEYS.PHONE,
  },
  {
    id: MANDATORY_CUSTOMER_INFO_KEYS.EMAIL,
    text: t(
      'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.email'
    ),
    value: MANDATORY_CUSTOMER_INFO_KEYS.EMAIL,
  },
  {
    id: MANDATORY_CUSTOMER_INFO_KEYS.BOTH,
    text: t(
      'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.both'
    ),
    value: MANDATORY_CUSTOMER_INFO_KEYS.BOTH,
  },
];

export const ADDITIONAL_INFO = (t: TFunction) => [
  {
    id: ADDITIONAL_INFO_KEYS.CUSTOMER_NAME,
    text: t(
      'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.collectCustomerName'
    ),
    value: SINGLE_PAYMENTS_FIELDS.COLLECT_CUSTOMER_NAME,
  },
  {
    id: ADDITIONAL_INFO_KEYS.SHIPPING_ADDRESS,
    text: t(
      'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.collectShippingAddress'
    ),
    value: SINGLE_PAYMENTS_FIELDS.COLLECT_SHIPPING_DETAILS,
  },
  {
    id: ADDITIONAL_INFO_KEYS.BILLING_ADDRESS,
    text: t(
      'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.collectBillingAddress'
    ),
    value: SINGLE_PAYMENTS_FIELDS.COLLECT_BILLING_DETAILS,
  },
];

export const EXPIRATION_DATE_INITIAL_VALUES = {
  [SINGLE_PAYMENTS_FIELDS.DATE]: {
    ...BASE_FIELDS_DATA,
    validations: [required],
  },
  [SINGLE_PAYMENTS_FIELDS.TIME]: {
    ...BASE_FIELDS_DATA,
    value: DEFAULT_TIME,
    validations: [required],
  },
};

export const PAYMENT_NUMBER_INITIAL_VALUE = {
  [SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT]: {
    value: '1',
    isValid: true,
    errorMsg: '',
    validations: [
      required,
      numbersOnly,
      {
        pattern: /^[1-9]\d*$/,
        error_msg: i18n.t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.restrictions.errors.minimumPaymentNumber'
        ),
      },
    ],
  },
};

export const ADVANCED_OPTIONS_INITIAL_VALUES = (t: TFunction) => ({
  [SINGLE_PAYMENTS_FIELDS.IMAGE]: {
    ...BASE_FIELDS_DATA,
    validations: [],
  },
  [SINGLE_PAYMENTS_FIELDS.DESCRIPTION]: {
    ...BASE_FIELDS_DATA,
    validations: [
      {
        pattern: DISALLOWED_HTML_REGEX,
        error_msg: t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.advancedOptions.errors.htmlValidationError'
        ),
      },
      {
        pattern: DESCRIPTION_REGEX,
        error_msg: t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.advancedOptions.errors.descLangError'
        ),
      },
    ],
  },
  [SINGLE_PAYMENTS_FIELDS.REF_ID]: {
    ...BASE_FIELDS_DATA,
    validations: [
      {
        pattern: DISALLOWED_HTML_REGEX,
        error_msg: t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.advancedOptions.errors.htmlValidationError'
        ),
      },
      {
        pattern: /^.{0,50}$/,
        error_msg: t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.advancedOptions.errors.refIdLengthError'
        ),
      },
    ],
  },
});

export const SINGLE_PL_SECTIONS_DIMENSIONS = {
  PAYMENT: {
    dimensionsList: [
      { width: '100%', height: '4rem', marginBottom: '1.25rem' },
      { width: '11.5rem', height: '3.25rem', marginBottom: '-0.75rem' },
      { width: '15.6rem', height: '1rem' },
    ],
  },
  CUSTOMER_DETAILS: {
    dimensionsList: [
      { width: '8.25rem', height: '1.75rem' },
      { width: '100%', height: '2.625rem' },
      { width: '19rem', height: '1.5rem', marginBottom: '.75rem' },
    ],
  },
  RESTRICTIONS: {
    dimensionsList: [
      { width: '6rem', height: '1.25rem' },
      { width: '11rem', height: '1.5rem', marginBottom: '-0.75rem' },
      { width: '21rem', height: '1.5rem' },
    ],
  },
  ADVANCED_OPTIONS: {
    dimensionsList: [{ width: '100%', height: '1.75rem' }],
  },
};

export const MAX_SHARE_WITH_ATTEMPTS = 2147483647;
export const MAX_VISIBLE_COUNTRIES = 4;
export const MAX_VISIBLE_COUNTRIES_MOBILE = 2;
